// Mirrors the backend `UserRole` serializer (the real `/personal-info/base/`
// payload). The retired `is_ot_admin` / `is_ot_agent` / `is_listing_*` flags were
// removed once every reader was migrated to these canonical flags / capabilities —
// keeping them out means a stale read can no longer compile.
//
// Deliberately a CLOSED type alias — no index signature. A type alias (unlike an
// interface) gets an implicit index signature, so `Profile` stays assignable to
// the capabilities lib's open `RoleFlags` view without opening reads up: a retired
// or misspelled flag (`is_ot_admin`, `is_offce_manager`) is still a compile error.
// Unknown backend flags still flow through at runtime (the capabilities resolver
// reads them structurally via the generated `ReqRole` union); a new role needs a
// line here only when .ts code reads its flag directly.
export type ProfileType = {
  is_company_admin: boolean;
  is_teamlead: boolean;
  is_agent: boolean;
  is_office_manager: boolean;
};

/**
 * The oversight tier: company_admin | teamlead | office_manager — the single
 * definition of the leadership tier for role-only display/identity rules
 * (deal-matrix overrides, agent management, report columns, the
 * contacts_assigned_agent ngx-permission). Action gates with a permission code
 * belong in the capabilities registry instead (e.g. canEditDealInvoice).
 * A tier change is an edit here, not a hunt across components.
 */
export const hasOversightRole = (
  roles: Partial<ProfileType> | null | undefined
): boolean =>
  !!(roles?.is_company_admin || roles?.is_teamlead || roles?.is_office_manager);

export class Profile {
  public get firstName() {
    if (!this.first_name) {
      return 'Guest';
    }
    return this.first_name;
  }

  public id: number;
  public email: string;
  public first_name: string;
  public last_name: string;
  public types: string[];
  public roles: ProfileType;
  // Snake_case permission CODES from the backend (deal_management, payload, …).
  // Resolved through `@tenant/capabilities`; raw code reads use the literal code.
  public permissions: string[];

  constructor(json: any) {
    const { id, email, first_name, last_name, types, roles, permissions } =
      json;
    Object.assign(this, json);
    this.id = id;
    this.email = email;
    this.first_name = first_name;
    this.last_name = last_name;
    this.types = types;
    this.roles = roles;
    this.permissions = permissions;
  }
}
